import axios from 'axios';

export type CommunityProfileAgeRange =
  | 'moins_18'
  | '18_30'
  | '31_45'
  | '46_60'
  | '61_plus';

export type CommunityProfileGender = 'feminin' | 'masculin' | 'autre';

export type CommunityContributionType = 'audio' | 'transcription' | 'questionnaire';

export type CommunityProfileNetwork = 'FLOOZ' | 'TMONEY';

export interface CommunityProfile {
  id: string;
  code: string;
  ageRange: CommunityProfileAgeRange;
  gender: CommunityProfileGender;
  location: string;
  collectorId: string;
  createdAt: string;
  phoneNumber?: string;
  network?: CommunityProfileNetwork;
}

export interface CommunityProfileSmsStatus {
  attempted: boolean;
  sent: boolean;
  reason?: string;
}

export interface CommunityProfileCreationResult {
  profile: CommunityProfile;
  sms: CommunityProfileSmsStatus;
}

export interface CommunityContribution {
  id: string;
  communityProfileId: string;
  collectorId: string;
  type: CommunityContributionType;
  content: string;
  createdAt: string;
}

export function getErrorMessageFromSmsReason(reason?: string): string {
  const reasonMap: Record<string, string> = {
    no_phone_number: 'Aucun numéro fourni',
    sms_config_missing: 'Configuration SMS manquante (contacter admin)',
    sms_send_failed: 'Échec lors de l\'envoi du SMS',
    invalid_phone_number: 'Numéro de téléphone invalide',
    provider_error: 'Erreur du fournisseur SMS',
    timeout: 'Délai d\'attente dépassé',
    auth_failed: 'Authentification SMS échouée',
    rate_limit: 'Trop de SMS envoyés (limite atteinte)',
  };

  if (!reason) {
    return 'Erreur inconnue lors de l\'envoi';
  }

  return reasonMap[reason] || reason;
}

export interface CollectorContributionContext {
  fromCollector?: boolean;
  contributionType?: CommunityContributionType;
  collectorId?: string;
  communityProfileId?: string;
  communityProfileCode?: string;
  communityProfileLocation?: string;
  returnTo?: string;
}

export interface CommunityCollectorDashboardSummary {
  profiles: number;
  contributions: number;
  today: number;
  feedback: number;
}

export interface CommunityCollectorAgeSexRow {
  ageRange: CommunityProfileAgeRange;
  feminin: number;
  masculin: number;
  autre: number;
  total: number;
}

export interface CommunityCollectorAgeSexSummary {
  feminin: number;
  masculin: number;
  autre: number;
  total: number;
}

export interface CommunityCollectorDashboardData {
  summary: CommunityCollectorDashboardSummary;
  ageSexSummary: CommunityCollectorAgeSexSummary;
  ageSexDistribution: CommunityCollectorAgeSexRow[];
  collecteurId?: string;
}

interface CollectorStorageShape {
  sequence: number;
  profiles: CommunityProfile[];
  contributions: CommunityContribution[];
}

type RawCommunityProfile = {
  id?: string | number;
  communityProfileId?: string | number;
  code?: string;
  tranche_age?: string;
  ageRange?: string;
  sexe?: string;
  gender?: string;
  localite?: string;
  location?: string;
  collecteur_id?: string | number;
  collectorId?: string | number;
  date_creation?: string;
  createdAt?: string;
  phoneNumber?: string;
  phone?: string;
  msisdn?: string;
  network?: string;
};

const STORAGE_KEY = 'yodi-community-collector-v1';
const API_BASE_URL = import.meta.env.VITE_API_URL || '/v1';

const collectorAxios = axios.create({
  baseURL: API_BASE_URL,
});

const readAuthToken = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const currentToken = window.localStorage.getItem('yodi-token');
  if (currentToken) {
    return currentToken;
  }

  const legacyToken = window.localStorage.getItem('token');
  if (legacyToken) {
    window.localStorage.setItem('yodi-token', legacyToken);
    window.localStorage.removeItem('token');
    return legacyToken;
  }

  return null;
};

collectorAxios.interceptors.request.use((config) => {
  if (typeof window !== 'undefined') {
    const token = readAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  return config;
});

const shouldRetryCollectorDashboardRequest = (error: unknown) =>
  axios.isAxiosError(error) && error.response?.status === 400;

const getCollectorDashboardParamsVariants = (collectorId?: string) => {
  if (!collectorId) {
    return [undefined];
  }

  return [
    { collecteurId: collectorId },
    { collectorId },
    undefined,
  ];
};

const getCollectorDashboardData = async (path: string, collectorId?: string) => {
  const variants = getCollectorDashboardParamsVariants(collectorId);
  let lastError: unknown = null;

  for (const params of variants) {
    try {
      const { data } = await collectorAxios.get(path, { params });
      return data;
    } catch (error) {
      lastError = error;
      if (!shouldRetryCollectorDashboardRequest(error)) {
        throw error;
      }
    }
  }

  throw lastError;
};

const defaultStorage = (): CollectorStorageShape => ({
  sequence: 46,
  profiles: [],
  contributions: [],
});

const readStorage = (): CollectorStorageShape => {
  if (typeof window === 'undefined') {
    return defaultStorage();
  }

  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return defaultStorage();
  }

  try {
    const parsed = JSON.parse(raw) as Partial<CollectorStorageShape>;
    return {
      sequence: typeof parsed.sequence === 'number' ? parsed.sequence : 46,
      profiles: Array.isArray(parsed.profiles) ? parsed.profiles : [],
      contributions: Array.isArray(parsed.contributions) ? parsed.contributions : [],
    };
  } catch {
    return defaultStorage();
  }
};

const writeStorage = (payload: CollectorStorageShape) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
};

const nextCode = (sequence: number) => `CM-${String(sequence).padStart(4, '0')}`;

const toOptionalString = (value: unknown) => {
  if (value === null || value === undefined) {
    return undefined;
  }

  const normalized = String(value).trim();
  return normalized || undefined;
};

const normalizeAgeRange = (value: unknown): CommunityProfileAgeRange => {
  switch (value) {
    case 'moins_18':
    case 'LESS_THAN_18':
    case '<18':
      return 'moins_18';
    case '31_45':
      return '31_45';
    case '46_60':
      return '46_60';
    case '61_plus':
    case '61+':
      return '61_plus';
    case '18_30':
    default:
      return '18_30';
  }
};

const normalizeNetwork = (value: unknown): CommunityProfileNetwork | undefined => {
  if (value === 'FLOOZ' || value === 'TMONEY') {
    return value;
  }
  return undefined;
};

const normalizeGender = (value: unknown): CommunityProfileGender => {
  if (value === 'feminin' || value === 'FEMININ' || value === 'female' || value === 'FEMALE') {
    return 'feminin';
  }

  if (value === 'autre' || value === 'AUTRE' || value === 'other' || value === 'OTHER') {
    return 'autre';
  }

  return 'masculin';
};

const toNumber = (value: unknown) => {
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : 0;
};

const normalizeAgeRangeFromDashboard = (value: unknown): CommunityProfileAgeRange =>
  normalizeAgeRange(typeof value === 'string' ? value.replace(/\s+/g, '_') : value);

const normalizeAgeSexRow = (value: unknown): CommunityCollectorAgeSexRow | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const record = value as Record<string, unknown>;
  const ageRange = normalizeAgeRangeFromDashboard(
    record.ageRange ?? record.age_range ?? record.trancheAge ?? record.tranche_age ?? record.label
  );
  const feminin = toNumber(record.feminin ?? record.female ?? record.women ?? record.femmes);
  const masculin = toNumber(record.masculin ?? record.male ?? record.men ?? record.hommes);
  const autre = toNumber(record.autre ?? record.other ?? record.others ?? record.autres);
  const total = toNumber(record.total ?? feminin + masculin + autre);

  return {
    ageRange,
    feminin,
    masculin,
    autre,
    total,
  };
};

const normalizeAgeSexSummary = (
  summary: unknown,
  rows: CommunityCollectorAgeSexRow[]
): CommunityCollectorAgeSexSummary => {
  if (summary && typeof summary === 'object') {
    const record = summary as Record<string, unknown>;
    const feminin = toNumber(record.feminin ?? record.female ?? record.women ?? record.femmes);
    const masculin = toNumber(record.masculin ?? record.male ?? record.men ?? record.hommes);
    const autre = toNumber(record.autre ?? record.other ?? record.others ?? record.autres);
    const total = toNumber(record.total ?? feminin + masculin + autre);

    return { feminin, masculin, autre, total };
  }

  return rows.reduce<CommunityCollectorAgeSexSummary>(
    (accumulator, row) => ({
      feminin: accumulator.feminin + row.feminin,
      masculin: accumulator.masculin + row.masculin,
      autre: accumulator.autre + row.autre,
      total: accumulator.total + row.total,
    }),
    { feminin: 0, masculin: 0, autre: 0, total: 0 }
  );
};

const normalizeDashboardSummary = (summary: unknown): CommunityCollectorDashboardSummary => {
  if (!summary || typeof summary !== 'object') {
    return { profiles: 0, contributions: 0, today: 0, feedback: 0 };
  }

  const record = summary as Record<string, unknown>;
  return {
    profiles: toNumber(record.profiles ?? record.profilesCount),
    contributions: toNumber(record.contributions ?? record.contributionsCount),
    today: toNumber(record.today ?? record.todayContributions),
    feedback: toNumber(record.feedback ?? record.returningProfiles ?? record.rejected ?? record.rejectedCount),
  };
};

const normalizeDashboardPayload = (payload: unknown): CommunityCollectorDashboardData => {
  const record = payload && typeof payload === 'object'
    ? ((payload as Record<string, unknown>).data as Record<string, unknown> | undefined) ?? (payload as Record<string, unknown>)
    : {};

  const rowsSource = Array.isArray(record.ageSexDistribution)
    ? record.ageSexDistribution
    : Array.isArray(record.rows)
      ? record.rows
      : [];

  const rows = rowsSource
    .map((entry) => normalizeAgeSexRow(entry))
    .filter((entry): entry is CommunityCollectorAgeSexRow => entry !== null);

  return {
    summary: normalizeDashboardSummary(record.summary),
    ageSexSummary: normalizeAgeSexSummary(record.ageSexSummary ?? record.summary, rows),
    ageSexDistribution: rows,
    collecteurId: toOptionalString(record.collecteurId ?? record.collectorId),
  };
};

const normalizeProfile = (raw: RawCommunityProfile): CommunityProfile => ({
  id: String(raw.id ?? raw.communityProfileId ?? ''),
  code: String(raw.code ?? '').toUpperCase(),
  ageRange: normalizeAgeRange(raw.tranche_age ?? raw.ageRange),
  gender: normalizeGender(raw.sexe ?? raw.gender),
  location: String(raw.localite ?? raw.location ?? '').trim(),
  collectorId: String(raw.collecteur_id ?? raw.collectorId ?? ''),
  createdAt: String(raw.date_creation ?? raw.createdAt ?? new Date().toISOString()),
  phoneNumber: toOptionalString(raw.phoneNumber ?? raw.phone ?? raw.msisdn),
  network: normalizeNetwork(raw.network),
});

const rememberProfile = (profile: CommunityProfile) => {
  const storage = readStorage();
  const existingWithoutCurrent = storage.profiles.filter(
    (entry) => entry.id !== profile.id && entry.code !== profile.code
  );
  const numericPart = Number(profile.code.replace(/\D/g, ''));

  writeStorage({
    ...storage,
    sequence: Number.isFinite(numericPart) && numericPart > storage.sequence ? numericPart : storage.sequence,
    profiles: [profile, ...existingWithoutCurrent],
  });

  return profile;
};

const unwrapProfilePayload = (payload: unknown): RawCommunityProfile => {
  if (!payload || typeof payload !== 'object') {
    return {};
  }

  const record = payload as Record<string, unknown>;
  const nested = record.data;
  if (nested && typeof nested === 'object') {
    return nested as RawCommunityProfile;
  }

  return record as RawCommunityProfile;
};

const normalizeSmsStatus = (
  value: unknown,
  phoneNumber?: string
): CommunityProfileSmsStatus => {
  const fallbackNoPhone: CommunityProfileSmsStatus = {
    attempted: false,
    sent: false,
    reason: 'no_phone_number',
  };

  if (!value || typeof value !== 'object') {
    return phoneNumber ? { attempted: false, sent: false } : fallbackNoPhone;
  }

  const record = value as Record<string, unknown>;
  const reason =
    typeof record.reason === 'string' && record.reason.trim()
      ? record.reason.trim()
      : undefined;

  return {
    attempted: record.attempted === true,
    sent: record.sent === true,
    reason,
  };
};

const unwrapCreateProfilePayload = (
  payload: unknown,
  phoneNumber?: string
): CommunityProfileCreationResult => {
  const profile = normalizeProfile(unwrapProfilePayload(payload));

  if (!payload || typeof payload !== 'object') {
    return {
      profile,
      sms: normalizeSmsStatus(undefined, phoneNumber),
    };
  }

  const record = payload as Record<string, unknown>;
  const nested =
    record.data && typeof record.data === 'object'
      ? (record.data as Record<string, unknown>)
      : undefined;

  return {
    profile,
    sms: normalizeSmsStatus(record.sms ?? nested?.sms, phoneNumber),
  };
};

const buildLocalDashboardData = (collectorId?: string): CommunityCollectorDashboardData => {
  const storage = readStorage();
  const profiles = collectorId
    ? storage.profiles.filter((profile) => profile.collectorId === collectorId)
    : storage.profiles;
  const contributions = collectorId
    ? storage.contributions.filter((contribution) => contribution.collectorId === collectorId)
    : storage.contributions;
  const today = new Date().toISOString().slice(0, 10);
  const contributionCountByProfile = new Map<string, number>();

  for (const contribution of contributions) {
    const currentCount = contributionCountByProfile.get(contribution.communityProfileId) ?? 0;
    contributionCountByProfile.set(contribution.communityProfileId, currentCount + 1);
  }

  const rows = (['moins_18', '18_30', '31_45', '46_60', '61_plus'] as CommunityProfileAgeRange[]).map((ageRange) => {
    let feminin = 0;
    let masculin = 0;
    let autre = 0;

    for (const profile of profiles) {
      if (profile.ageRange !== ageRange) {
        continue;
      }

      if (profile.gender === 'feminin') {
        feminin += 1;
      } else if (profile.gender === 'autre') {
        autre += 1;
      } else {
        masculin += 1;
      }
    }

    return {
      ageRange,
      feminin,
      masculin,
      autre,
      total: feminin + masculin + autre,
    };
  });

  const ageSexSummary = rows.reduce<CommunityCollectorAgeSexSummary>(
    (accumulator, row) => ({
      feminin: accumulator.feminin + row.feminin,
      masculin: accumulator.masculin + row.masculin,
      autre: accumulator.autre + row.autre,
      total: accumulator.total + row.total,
    }),
    { feminin: 0, masculin: 0, autre: 0, total: 0 }
  );

  return {
    summary: {
      profiles: profiles.length,
      contributions: contributions.length,
      today: contributions.filter((item) => item.createdAt.slice(0, 10) === today).length,
      feedback: Array.from(contributionCountByProfile.values()).filter((count) => count > 1).length,
    },
    ageSexSummary,
    ageSexDistribution: rows,
    collecteurId: collectorId,
  };
};

const ACTIVE_SESSION_KEY = 'yodi-active-collector-session';

function getPersistedSession(): { id?: string; code?: string; location?: string; collectorId?: string } | null {
  try {
    const raw = localStorage.getItem(ACTIVE_SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export const resolveCollectorContributionContext = (value: unknown): CollectorContributionContext | null => {
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const collectorId = toOptionalString(record.collectorId);
    const communityProfileId = toOptionalString(record.communityProfileId);
    const communityProfileCode = toOptionalString(record.communityProfileCode);
    const communityProfileLocation = toOptionalString(record.communityProfileLocation);
    const returnTo = toOptionalString(record.returnTo);
    const contributionType = record.contributionType;

    if (collectorId || communityProfileId || communityProfileCode) {
      return {
        fromCollector: record.fromCollector === true,
        contributionType:
          contributionType === 'audio' || contributionType === 'transcription' || contributionType === 'questionnaire'
            ? contributionType
            : undefined,
        collectorId,
        communityProfileId,
        communityProfileCode,
        communityProfileLocation,
        returnTo,
      };
    }
  }

  // Fallback: check for a persisted active collector session
  const persisted = getPersistedSession();
  if (persisted && (persisted.id || persisted.code)) {
    return {
      fromCollector: true,
      contributionType: undefined,
      collectorId: toOptionalString(persisted.collectorId),
      communityProfileId: toOptionalString(persisted.id),
      communityProfileCode: toOptionalString(persisted.code),
      communityProfileLocation: toOptionalString(persisted.location),
      returnTo: undefined,
    };
  }

  return null;
};

export const communityCollectorStorage = {
  getLocalDashboard(collecteurId?: string) {
    return buildLocalDashboardData(collecteurId);
  },

  getProfiles(collectorId?: string) {
    const storage = readStorage();
    if (!collectorId) {
      return storage.profiles;
    }

    return storage.profiles.filter((profile) => profile.collectorId === collectorId);
  },

  async findProfileByCode(code: string) {
    const normalizedCode = code.trim().toUpperCase();
    if (!normalizedCode) {
      return null;
    }

    const storage = readStorage();
    const cached = storage.profiles.find((profile) => profile.code === normalizedCode) ?? null;
    if (cached) {
      return cached;
    }

    const { data } = await collectorAxios.get(`/community-profiles/code/${encodeURIComponent(normalizedCode)}`);
    const profile = normalizeProfile(unwrapProfilePayload(data));
    return rememberProfile(profile);
  },

  async createProfile(input: {
    ageRange: CommunityProfileAgeRange;
    gender: CommunityProfileGender;
    location: string;
    collectorId: string;
    phoneNumber?: string;
    network?: CommunityProfileNetwork;
  }) {
    const trimmedPhoneNumber = toOptionalString(input.phoneNumber);
    const payload = {
      trancheAge: input.ageRange,
      tranche_age: input.ageRange,
      sexe: input.gender,
      localite: input.location.trim(),
      phoneNumber: trimmedPhoneNumber,
      phone: trimmedPhoneNumber,
      msisdn: trimmedPhoneNumber,
      network: trimmedPhoneNumber ? input.network : undefined,
    };

    const { data } = await collectorAxios.post('/community-profiles', payload);
    const creationResult = unwrapCreateProfilePayload(data, trimmedPhoneNumber);
    const profile = creationResult.profile;

    if (!profile.id || !profile.code) {
      const storage = readStorage();
      const sequence = storage.sequence + 1;
      const rememberedProfile = rememberProfile({
        id: crypto.randomUUID(),
        code: nextCode(sequence),
        ageRange: input.ageRange,
        gender: input.gender,
        location: input.location.trim(),
        collectorId: input.collectorId,
        createdAt: new Date().toISOString(),
        phoneNumber: trimmedPhoneNumber,
        network: trimmedPhoneNumber ? input.network : undefined,
      });

      return {
        profile: rememberedProfile,
        sms: creationResult.sms,
      };
    }

    return {
      profile: rememberProfile(profile),
      sms: creationResult.sms,
    };
  },

  async updateProfile(
    profileId: string,
    input: { phoneNumber?: string; network?: CommunityProfileNetwork },
  ) {
    const normalizedId = String(profileId).trim();
    if (!normalizedId) {
      throw new Error('Missing profile id');
    }

    const payload: Record<string, unknown> = {};
    const trimmedPhoneNumber = toOptionalString(input.phoneNumber);
    if (trimmedPhoneNumber !== undefined) {
      payload.phoneNumber = trimmedPhoneNumber;
    }
    if (input.network) {
      payload.network = input.network;
    }

    const { data } = await collectorAxios.patch(`/community-profiles/${encodeURIComponent(normalizedId)}`, payload);
    const profile = normalizeProfile(unwrapProfilePayload(data));
    return rememberProfile(profile);
  },

  async findProfileById(profileId: string | number) {
    const normalizedId = String(profileId).trim();
    if (!normalizedId) {
      return null;
    }

    const storage = readStorage();
    const cached = storage.profiles.find((profile) => profile.id === normalizedId) ?? null;
    if (cached) {
      return cached;
    }

    const { data } = await collectorAxios.get(`/community-profiles/${encodeURIComponent(normalizedId)}`);
    const profile = normalizeProfile(unwrapProfilePayload(data));
    return rememberProfile(profile);
  },

  getContributions(profileId?: string, collectorId?: string) {
    const storage = readStorage();
    return storage.contributions.filter((contribution) => {
      if (profileId && contribution.communityProfileId !== profileId) {
        return false;
      }

      if (collectorId && contribution.collectorId !== collectorId) {
        return false;
      }

      return true;
    });
  },

  addContribution(input: {
    communityProfileId: string;
    collectorId: string;
    type: CommunityContributionType;
    content: string;
  }) {
    const storage = readStorage();
    const contribution: CommunityContribution = {
      id: crypto.randomUUID(),
      communityProfileId: input.communityProfileId,
      collectorId: input.collectorId,
      type: input.type,
      content: input.content.trim(),
      createdAt: new Date().toISOString(),
    };

    writeStorage({
      ...storage,
      contributions: [contribution, ...storage.contributions],
    });

    return contribution;
  },

  getCollectorStats(collectorId: string) {
    const localDashboard = buildLocalDashboardData(collectorId);

    return {
      profilesCount: localDashboard.summary.profiles,
      contributionsCount: localDashboard.summary.contributions,
      todayContributions: localDashboard.summary.today,
      returningProfiles: localDashboard.summary.feedback,
    };
  },

  async getDashboard(collecteurId?: string) {
    const data = await getCollectorDashboardData('/community-profiles/dashboard', collecteurId);

    return normalizeDashboardPayload(data);
  },

  async getAgeSexDistribution(collecteurId?: string) {
    const data = await getCollectorDashboardData('/community-profiles/dashboard/age-sex', collecteurId);

    const normalized = normalizeDashboardPayload({
      summary: undefined,
      ageSexSummary: (data as Record<string, unknown> | undefined)?.summary,
      ageSexDistribution: (data as Record<string, unknown> | undefined)?.rows,
      collecteurId: (data as Record<string, unknown> | undefined)?.collecteurId,
      data,
    });

    return {
      summary: normalized.ageSexSummary,
      rows: normalized.ageSexDistribution,
      totalProfiles: toNumber((data as Record<string, unknown> | undefined)?.totalProfiles ?? normalized.ageSexSummary.total),
      collecteurId: normalized.collecteurId,
    };
  },
};

export const rememberCollectorModuleContribution = (
  context: CollectorContributionContext | null | undefined,
  input: {
    type: CommunityContributionType;
    content: string;
  }
) => {
  if (!context?.collectorId || !context.communityProfileId) {
    return null;
  }

  const content = input.content.trim();
  if (!content) {
    return null;
  }

  return communityCollectorStorage.addContribution({
    communityProfileId: context.communityProfileId,
    collectorId: context.collectorId,
    type: input.type,
    content,
  });
};